import * as React from "react"
import { agentModelOptionStorageKey, agentProviderLabel, fetchAgentModelOptions, normalizeProvider } from "./api"
import { AgentChatPanel } from "./chat_panel"
import {
  appendAgentChatMessages,
  createAgentChatSession,
  downloadChatTranscriptTxt,
  fetchAgentChatSession,
  listAgentChatSessions,
  messagesFromAgentChatSession,
  rateAgentChatMessage,
  toAgentChatLogMessages,
  type AgentChatSessionSummary,
} from "./chat_log"
import {
  chatTimestamp,
  openingChatMessages,
  isAgentTurnAborted,
  randomMessageId,
  runAgentTurn,
} from "./runtime"
import type { AgentActions, AgentModelOption, ChatMessage } from "./types"
import { visibleAssistantContent } from "./workflow"
import { getChartDetailEntry } from "../editor/model_detail"
import { AegisChartSection } from "./chart_section"
import { setExpandedChartId } from "../snapshot/chart_detail_expand"
import type { Data } from "rete/types/core/data"
import { aegisOptionLines, usePublicShareSettings } from "../editor/public_share_settings"

export interface AegisSessionConfig {
  teamId?: number
  projectId?: number
  actions?: AgentActions
  /** Prefer a ref when the live `AgentActions` object is recreated often. */
  actionsRef?: React.MutableRefObject<AgentActions | undefined>
  /** True while the map view tab is active — used so `#id` clicks expand charts. */
  isMapViewRef?: React.MutableRefObject<boolean>
  /** Live project model, for resolving whether a `#id` click is a chart. */
  modelRef?: React.MutableRefObject<Data | null>
}

interface AegisSessionValue extends AegisSessionConfig {
  draft: string
  setDraft: (value: string) => void
  messages: ChatMessage[]
  isSending: boolean
  modelOptions: AgentModelOption[]
  selectedOptionId: string
  optionsLoading: boolean
  pastSessions: AgentChatSessionSummary[]
  historyOpen: boolean
  historyLoading: boolean
  activeSessionId: number | null
  agentReady: boolean
  selectedOption: AgentModelOption | undefined
  sendMessage: (override?: string, options?: { fromVoice?: boolean }) => void
  stopMessage: () => void
  handleRateMessage: (messageId: string, rating: 1 | -1) => void
  handleExportChat: () => void
  toggleHistoryOpen: () => void
  startNewChat: () => void
  loadChatSession: (sessionId: number) => void
  handleSelectedOptionChange: (optionId: string) => void
  formatSessionWhen: (iso: string) => string
  focusComponent: (id: number) => void
  /** Shared across map and model: AEGIS tab follows whichever view is active. */
  aegisTabOpen: boolean
  setAegisTabOpen: (open: boolean) => void
}

const PUBLIC_TOOL_STATUS = "Updating models"

const AegisSessionContext = React.createContext<AegisSessionValue | null>(null)

export function useAegisSession(): AegisSessionValue {
  const value = React.useContext(AegisSessionContext)
  if (!value) {
    throw new Error("useAegisSession must be used within an AegisSessionProvider")
  }
  return value
}

export function AegisSessionProvider({
  teamId,
  projectId,
  actions,
  actionsRef,
  isMapViewRef,
  modelRef,
  children,
}: AegisSessionConfig & { children: React.ReactNode }) {
  const publicShareSettings = usePublicShareSettings()
  const publicShareSettingsRef = React.useRef(publicShareSettings)
  publicShareSettingsRef.current = publicShareSettings
  const [draft, setDraft] = React.useState("")
  const [messages, setMessages] = React.useState<ChatMessage[]>(() =>
    openingChatMessages(publicShareSettings != null, publicShareSettings?.aegisIntro)
  )
  const [isSending, setIsSending] = React.useState(false)
  const [modelOptions, setModelOptions] = React.useState<AgentModelOption[]>([])
  const [selectedOptionId, setSelectedOptionId] = React.useState("")
  const [optionsLoading, setOptionsLoading] = React.useState(false)
  const chatSessionIdRef = React.useRef<number | null>(null)
  const abortRef = React.useRef<AbortController | null>(null)
  const persistQueueRef = React.useRef<Promise<void>>(Promise.resolve())
  const skipNextPersistRef = React.useRef(false)
  const [pastSessions, setPastSessions] = React.useState<AgentChatSessionSummary[]>([])
  const [historyOpen, setHistoryOpen] = React.useState(false)
  const [historyLoading, setHistoryLoading] = React.useState(false)
  const [activeSessionId, setActiveSessionId] = React.useState<number | null>(null)
  const [aegisTabOpen, setAegisTabOpen] = React.useState(false)
  const actionsPropRef = React.useRef(actions)
  actionsPropRef.current = actions
  const parentActionsRef = React.useRef(actionsRef)
  parentActionsRef.current = actionsRef
  const getLiveActions = React.useCallback(
    () => parentActionsRef.current?.current ?? actionsPropRef.current,
    []
  )
  const componentsCatalogRef = React.useRef<Promise<string> | null>(null)
  React.useEffect(() => {
    componentsCatalogRef.current = null
  }, [actions, actionsRef])

  React.useEffect(() => {
    if (publicShareSettings?.agentConfigured && publicShareSettings.shareCode && publicShareSettings.agentProvider && publicShareSettings.agentModel) {
      const provider = normalizeProvider(publicShareSettings.agentProvider)
      const option: AgentModelOption = {
        id: `share:${provider}:${publicShareSettings.agentModel}`,
        provider,
        providerLabel: agentProviderLabel(provider),
        keySource: "personal",
        keySourceLabel: "Shared key",
        model: publicShareSettings.agentModel,
        modelLabel: publicShareSettings.agentModelLabel || publicShareSettings.agentModel,
        label: publicShareSettings.agentModelLabel || publicShareSettings.agentModel,
      }
      setModelOptions([option])
      setSelectedOptionId(option.id)
      setOptionsLoading(false)
      return
    }

    if (teamId == null) {
      setModelOptions([])
      setSelectedOptionId("")
      return
    }

    let cancelled = false
    setOptionsLoading(true)
    fetchAgentModelOptions(teamId)
      .then(({ options, defaultId }) => {
        if (cancelled) return
        setModelOptions(options)
        const saved = localStorage.getItem(agentModelOptionStorageKey(teamId))
        const initial =
          saved && options.some(option => option.id === saved)
            ? saved
            : defaultId && options.some(option => option.id === defaultId)
              ? defaultId
              : options[0]?.id ?? ""
        setSelectedOptionId(initial)
      })
      .catch(err => {
        if (cancelled) return
        console.warn("[AEGIS] Failed to load model options", err)
        setModelOptions([])
        setSelectedOptionId("")
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [teamId, publicShareSettings])

  const selectedOption = React.useMemo(
    () => modelOptions.find(option => option.id === selectedOptionId),
    [modelOptions, selectedOptionId]
  )

  const persistChatSnapshot = React.useCallback(
    async (snapshot: ChatMessage[]) => {
      if (projectId == null || !selectedOption) return
      if (!snapshot.some(message => message.role === "user")) return

      try {
        if (chatSessionIdRef.current == null) {
          chatSessionIdRef.current = await createAgentChatSession(projectId, {
            provider: selectedOption.provider,
            model: selectedOption.model,
            keySource: selectedOption.keySource,
          })
          setActiveSessionId(chatSessionIdRef.current)
        }
        const sessionId = chatSessionIdRef.current
        if (sessionId == null) return
        await appendAgentChatMessages(
          projectId,
          sessionId,
          toAgentChatLogMessages(snapshot)
        )
        void listAgentChatSessions(projectId)
          .then(setPastSessions)
          .catch(() => undefined)
      } catch (err) {
        console.warn("[AEGIS] Failed to persist chat log", err)
      }
    },
    [projectId, selectedOption]
  )

  React.useEffect(() => {
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false
      return
    }
    const snapshot = messages
    persistQueueRef.current = persistQueueRef.current
      .then(() => persistChatSnapshot(snapshot))
      .catch(() => undefined)
  }, [messages, persistChatSnapshot])

  const refreshPastSessions = React.useCallback(async () => {
    if (projectId == null) {
      setPastSessions([])
      return [] as AgentChatSessionSummary[]
    }
    try {
      const sessions = await listAgentChatSessions(projectId)
      setPastSessions(sessions)
      return sessions
    } catch (err) {
      console.warn("[AEGIS] Failed to list chat sessions", err)
      return [] as AgentChatSessionSummary[]
    }
  }, [projectId])

  const loadChatSession = React.useCallback(
    async (sessionId: number) => {
      if (projectId == null || isSending) return
      setHistoryLoading(true)
      try {
        const detail = await fetchAgentChatSession(projectId, sessionId)
        const restored = messagesFromAgentChatSession(detail)
        chatSessionIdRef.current = detail.id
        setActiveSessionId(detail.id)
        skipNextPersistRef.current = true
        setMessages(restored.length > 0 ? restored : openingChatMessages(publicShareSettingsRef.current != null, publicShareSettingsRef.current?.aegisIntro))
        setHistoryOpen(false)
      } catch (err) {
        console.warn("[AEGIS] Failed to restore chat session", err)
      } finally {
        setHistoryLoading(false)
      }
    },
    [projectId, isSending]
  )

  const startNewChat = React.useCallback(() => {
    if (isSending) return
    chatSessionIdRef.current = null
    setActiveSessionId(null)
    skipNextPersistRef.current = true
    setMessages(openingChatMessages(publicShareSettingsRef.current != null, publicShareSettingsRef.current?.aegisIntro))
    setDraft("")
    setHistoryOpen(false)
  }, [isSending])

  React.useEffect(() => {
    let cancelled = false
    chatSessionIdRef.current = null
    setActiveSessionId(null)
    setHistoryOpen(false)
    skipNextPersistRef.current = true
    setMessages(openingChatMessages(publicShareSettingsRef.current != null, publicShareSettingsRef.current?.aegisIntro))
    setDraft("")

    if (projectId == null) {
      setPastSessions([])
      return
    }

    setHistoryLoading(true)
    listAgentChatSessions(projectId)
      .then(sessions => {
        if (!cancelled) setPastSessions(sessions)
      })
      .catch(err => {
        if (!cancelled) console.warn("[AEGIS] Failed to list chat history", err)
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [projectId])

  const handleRateMessage = React.useCallback(
    (messageId: string, rating: 1 | -1) => {
      let nextRating: 1 | -1 | undefined
      setMessages(prev => {
        const next = prev.map(message => {
          if (message.id !== messageId) return message
          nextRating = message.rating === rating ? undefined : rating
          return { ...message, rating: nextRating }
        })
        void persistChatSnapshot(next).then(async () => {
          const sessionId = chatSessionIdRef.current
          if (projectId == null || sessionId == null) return
          try {
            const saved = await rateAgentChatMessage(
              projectId,
              sessionId,
              messageId,
              nextRating ?? null
            )
            setMessages(current =>
              current.map(message =>
                message.id === messageId
                  ? { ...message, rating: saved === 1 || saved === -1 ? saved : undefined }
                  : message
              )
            )
          } catch (err) {
            console.warn("[AEGIS] Failed to save message rating", err)
          }
        })
        return next
      })
    },
    [persistChatSnapshot, projectId]
  )

  const handleExportChat = React.useCallback(() => {
    downloadChatTranscriptTxt(messages, { modelLabel: selectedOption?.label })
  }, [messages, selectedOption])

  const toggleHistoryOpen = React.useCallback(() => {
    if (pastSessions.length === 0) return
    setHistoryOpen(prev => {
      const next = !prev
      if (next) void refreshPastSessions()
      return next
    })
  }, [pastSessions.length, refreshPastSessions])

  const formatSessionWhen = React.useCallback((iso: string) => {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return iso
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }, [])

  const handleSelectedOptionChange = React.useCallback((optionId: string) => {
    setSelectedOptionId(optionId)
    if (teamId != null) {
      localStorage.setItem(agentModelOptionStorageKey(teamId), optionId)
    }
  }, [teamId])

  const agentReady = modelOptions.length > 0 && !!selectedOption

  const stopMessage = React.useCallback(() => {
    abortRef.current?.abort()
  }, [])

  React.useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const sendMessage = React.useCallback(async (override?: string, options?: { fromVoice?: boolean }) => {
    const text = (override ?? draft).trim()
    if (!text || isSending || !selectedOption) return

    const userMessage: ChatMessage = {
      id: randomMessageId(),
      role: "user",
      text,
      timestamp: chatTimestamp(),
      ...(options?.fromVoice ? { fromVoice: true } : {}),
    }
    const history = [...messages, userMessage]
    setMessages(history)
    setDraft("")
    setIsSending(true)
    const startedAt = Date.now()
    const controller = new AbortController()
    abortRef.current = controller
    let structuralChange = false
    let modelBefore = ""

    try {
      const liveActions = getLiveActions()
      modelBefore = liveActions?.getReadableModel?.() ?? ""
      const readableModel = modelBefore
      let readableComponents = ""
      if (liveActions?.getReadableComponents) {
        if (!componentsCatalogRef.current) {
          componentsCatalogRef.current = liveActions
            .getReadableComponents()
            .catch(err => {
              componentsCatalogRef.current = null
              console.warn("[AEGIS] Failed to build component catalogue", err)
              return ""
            })
        }
        readableComponents = await componentsCatalogRef.current
        if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError")
      }

      const reply = await runAgentTurn({
        teamId,
        provider: selectedOption.provider,
        model: selectedOption.model,
        keySource: selectedOption.keySource,
        shareCode: publicShareSettingsRef.current?.agentConfigured ? publicShareSettingsRef.current.shareCode : undefined,
        history,
        actions: liveActions,
        readableModel,
        readableComponents,
        extraInstructions: publicShareSettingsRef.current?.aegisInstructions,
        publicView: publicShareSettingsRef.current != null,
        signal: controller.signal,
        onToolEvent: (label) => {
          if (/^(Added|Removed|Connected|Disconnected)\b/.test(label)) {
            structuralChange = true
          }
          if (publicShareSettingsRef.current != null) {
            const status = label.startsWith("Switched area") || label.startsWith("Already showing")
              ? "Switching area"
              : PUBLIC_TOOL_STATUS
            setMessages(prev => {
              const last = prev[prev.length - 1]
              if (last?.role === "tool" && last.text === status) return prev
              return [
                ...prev,
                {
                  id: randomMessageId(),
                  role: "tool",
                  text: status,
                  timestamp: chatTimestamp(),
                },
              ]
            })
            return
          }
          setMessages(prev => [
            ...prev,
            {
              id: randomMessageId(),
              role: "tool",
              text: label,
              timestamp: chatTimestamp(),
            },
          ])
        },
        onPlan: (plan) => {
          if (publicShareSettingsRef.current != null) return
          setMessages(prev => [
            ...prev,
            {
              id: randomMessageId(),
              role: "plan",
              text: plan,
              timestamp: chatTimestamp(),
            },
          ])
        },
        onReasoning: (text) => {
          if (publicShareSettingsRef.current != null) return
          setMessages(prev => [
            ...prev,
            {
              id: randomMessageId(),
              role: "thinking",
              text,
              timestamp: chatTimestamp(),
            },
          ])
        },
      })
      const visibleReply = visibleAssistantContent(reply)
      const durationMs = Date.now() - startedAt
      if (visibleReply) {
        setMessages(prev => [
          ...prev,
          {
            id: randomMessageId(),
            role: "assistant",
            text: visibleReply,
            timestamp: chatTimestamp(),
            durationMs,
          },
        ])
      } else {
        setMessages(prev => {
          if (prev.length === 0) return prev
          const last = prev[prev.length - 1]
          return [...prev.slice(0, -1), { ...last, durationMs }]
        })
      }
    } catch (err) {
      const durationMs = Date.now() - startedAt
      if (isAgentTurnAborted(err)) {
        setMessages(prev => [
          ...prev,
          {
            id: randomMessageId(),
            role: "assistant",
            text: "Stopped.",
            timestamp: chatTimestamp(),
            durationMs,
          },
        ])
      } else {
        const message = err instanceof Error ? err.message : String(err)
        setMessages(prev => [
          ...prev,
          {
            id: randomMessageId(),
            role: "assistant",
            text: `⚠️ ${message}`,
            timestamp: chatTimestamp(),
            durationMs,
          },
        ])
      }
    } finally {
      const actions = getLiveActions()
      if (structuralChange) actions?.autoLayout?.()
      if (publicShareSettingsRef.current != null) {
        const modelAfter = actions?.getReadableModel?.() ?? ""
        if (modelAfter !== modelBefore) {
          actions?.requestProcess?.()
        }
      }
      if (abortRef.current === controller) abortRef.current = null
      setIsSending(false)
    }
  }, [draft, isSending, messages, teamId, selectedOption, getLiveActions])

  const focusComponent = React.useCallback((id: number) => {
    if (isMapViewRef?.current) {
      const chart = getChartDetailEntry(modelRef?.current ?? null, id)
      if (chart != null) {
        setExpandedChartId(id)
        return
      }
    }
    getLiveActions()?.focusComponent(id)
  }, [getLiveActions, isMapViewRef, modelRef])

  const value = React.useMemo<AegisSessionValue>(
    () => ({
      teamId,
      projectId,
      actions: getLiveActions(),
      draft,
      setDraft,
      messages,
      isSending,
      modelOptions,
      selectedOptionId,
      optionsLoading,
      pastSessions,
      historyOpen,
      historyLoading,
      activeSessionId,
      agentReady,
      selectedOption,
      sendMessage,
      stopMessage,
      handleRateMessage,
      handleExportChat,
      toggleHistoryOpen,
      startNewChat,
      loadChatSession,
      handleSelectedOptionChange,
      formatSessionWhen,
      focusComponent,
      aegisTabOpen,
      setAegisTabOpen,
    }),
    [
      teamId,
      projectId,
      draft,
      messages,
      isSending,
      modelOptions,
      selectedOptionId,
      optionsLoading,
      pastSessions,
      historyOpen,
      historyLoading,
      activeSessionId,
      agentReady,
      selectedOption,
      sendMessage,
      stopMessage,
      handleRateMessage,
      handleExportChat,
      toggleHistoryOpen,
      startNewChat,
      loadChatSession,
      handleSelectedOptionChange,
      formatSessionWhen,
      focusComponent,
      aegisTabOpen,
    ]
  )

  return (
    <AegisSessionContext.Provider value={value}>
      {children}
    </AegisSessionContext.Provider>
  )
}

/**
 * Use an existing AEGIS session when one is already in the tree (project
 * editor sharing map + model tabs). Otherwise create a standalone provider
 * so the model sidebar still works in embeds that don't wrap the editor.
 */
export function AegisSessionBoundary({
  teamId,
  projectId,
  actions,
  actionsRef,
  children,
}: AegisSessionConfig & { children: React.ReactNode }) {
  const existing = React.useContext(AegisSessionContext)
  if (existing) return <>{children}</>
  return (
    <AegisSessionProvider teamId={teamId} projectId={projectId} actions={actions} actionsRef={actionsRef}>
      {children}
    </AegisSessionProvider>
  )
}

export function AegisChatToolbar() {
  const {
    historyOpen,
    pastSessions,
    toggleHistoryOpen,
    startNewChat,
    isSending,
    stopMessage,
    handleExportChat,
    messages,
  } = useAegisSession()
  const publicView = usePublicShareSettings() != null

  return (
    <>
      {isSending && (
        <button
          type="button"
          className="model-editor-chat-icon-btn"
          onClick={stopMessage}
          aria-label="Stop generating"
          title="Stop"
        >
          <i className="fas fa-stop" />
        </button>
      )}
      {!publicView && (
        <>
          <button
            type="button"
            className={`model-editor-chat-icon-btn model-editor-chat-history-btn${
              historyOpen ? " model-editor-chat-icon-btn--active" : ""
            }`}
            onClick={toggleHistoryOpen}
            aria-label={
              pastSessions.length > 0
                ? `Chat history (${pastSessions.length})`
                : "No chat history"
            }
            title={
              pastSessions.length > 0
                ? `Chat history (${pastSessions.length})`
                : "No chat history yet"
            }
            aria-expanded={historyOpen}
            disabled={pastSessions.length === 0}
          >
            <i className="fas fa-clock-rotate-left" aria-hidden />
            {pastSessions.length > 0 && (
              <span className="model-editor-chat-history-count" aria-hidden>
                {pastSessions.length > 99 ? "99+" : pastSessions.length}
              </span>
            )}
          </button>
          <button
            type="button"
            className="model-editor-chat-icon-btn"
            onClick={startNewChat}
            aria-label="Start a new chat"
            title="New chat"
            disabled={isSending}
          >
            <i className="fas fa-plus" />
          </button>
          <button
            type="button"
            className="model-editor-chat-icon-btn"
            onClick={handleExportChat}
            aria-label="Export chat as text file"
            title="Export chat (.txt)"
            disabled={!messages.some(message => message.role === "user")}
          >
            <i className="fas fa-download" />
          </button>
        </>
      )}
    </>
  )
}

export function AegisChatHistory() {
  const {
    historyOpen,
    startNewChat,
    isSending,
    historyLoading,
    pastSessions,
    activeSessionId,
    loadChatSession,
    formatSessionWhen,
  } = useAegisSession()

  if (!historyOpen) return null

  return (
    <div className="model-editor-chat-history" role="dialog" aria-label="AEGIS chat history">
      <div className="model-editor-chat-history-header">
        <span>Your chats on this project</span>
        <button
          type="button"
          className="model-editor-chat-history-new"
          onClick={startNewChat}
          disabled={isSending}
        >
          <i className="fas fa-plus" aria-hidden /> New chat
        </button>
      </div>
      {historyLoading && pastSessions.length === 0 ? (
        <p className="model-editor-chat-history-empty">Loading…</p>
      ) : pastSessions.length === 0 ? (
        <p className="model-editor-chat-history-empty">No saved chats yet.</p>
      ) : (
        <ul className="model-editor-chat-history-list">
          {pastSessions.map(session => {
            const isActive = session.id === activeSessionId
            return (
              <li key={session.id}>
                <button
                  type="button"
                  className={`model-editor-chat-history-item${
                    isActive ? " model-editor-chat-history-item--active" : ""
                  }`}
                  onClick={() => void loadChatSession(session.id)}
                  disabled={isSending || historyLoading}
                >
                  <span className="model-editor-chat-history-item-when">
                    {formatSessionWhen(session.updated_at || session.created_at)}
                  </span>
                  <span className="model-editor-chat-history-item-preview">
                    {session.preview || "Empty chat"}
                  </span>
                  <span className="model-editor-chat-history-item-meta">
                    {session.message_count} msg
                    {session.model ? ` · ${session.model}` : ""}
                    {isActive ? " · open" : ""}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export function AegisChatBody() {
  const {
    draft,
    setDraft,
    messages,
    isSending,
    sendMessage,
    stopMessage,
    agentReady,
    modelOptions,
    selectedOptionId,
    handleSelectedOptionChange,
    optionsLoading,
    teamId,
    focusComponent,
    handleRateMessage,
  } = useAegisSession()
  const publicShareSettings = usePublicShareSettings()
  const listRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    const el = listRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages])

  return (
    <AgentChatPanel
      listRef={listRef}
      messages={messages}
      draft={draft}
      setDraft={setDraft}
      isSending={isSending}
      onSend={sendMessage}
      onStop={stopMessage}
      configured={agentReady}
      modelOptions={modelOptions}
      selectedOptionId={selectedOptionId}
      onSelectedOptionChange={handleSelectedOptionChange}
      optionsLoading={optionsLoading}
      teamId={teamId}
      onActivateRef={focusComponent}
      onRateMessage={handleRateMessage}
      publicView={publicShareSettings != null}
      quickPrompts={publicShareSettings ? aegisOptionLines(publicShareSettings.aegisOptions) : []}
      showExplainMap={publicShareSettings ? publicShareSettings.showExplainMap !== false : true}
    />
  )
}

/**
 * Full AEGIS chat chrome used by the map-view sidebar tab — same header
 * actions, history picker, and transcript as the model-view AEGIS tab.
 */
export function AegisMapTab({
  model = null,
  floating = false,
  collapsed = false,
  onCollapse,
  onClose,
}: {
  model?: Data | null
  floating?: boolean
  collapsed?: boolean
  onCollapse?: () => void
  onClose?: () => void
} = {}) {
  const { activeSessionId } = useAegisSession()

  return (
    <div
      className="model-editor d-flex flex-column flex-grow-1"
      style={{ minHeight: 0, overflow: "hidden" }}
    >
      <aside
        className="model-editor-chat"
        aria-label="AEGIS"
        style={{
          width: "100%",
          minWidth: 0,
          maxWidth: "none",
          borderLeft: "none",
          flex: "1 1 auto",
          height: collapsed || floating ? "auto" : "100%",
        }}
      >
        <div className="model-editor-chat-top">
          <header className="model-editor-chat-header">
            <span className="model-editor-chat-avatar" aria-hidden>
              <i className={`fas ${floating ? "fa-robot" : "fa-comments"}`} />
            </span>
            <div className="model-editor-chat-heading">
              <span className="model-editor-chat-title">AEGIS</span>
              {floating ? (
                <span className="model-editor-chat-subtitle">AI Agent for LIMMMA</span>
              ) : activeSessionId != null ? (
                <span className="model-editor-chat-subtitle">Continuing earlier chat</span>
              ) : null}
            </div>
            <AegisChatToolbar />
            {onCollapse && (
              <button
                type="button"
                className="model-editor-chat-icon-btn"
                onClick={onCollapse}
                aria-label={collapsed ? "Expand AEGIS" : "Collapse AEGIS"}
              >
                <i className={`fas fa-chevron-${collapsed ? "down" : "up"}`} />
              </button>
            )}
            {onClose && (
              <button
                type="button"
                className="model-editor-chat-icon-btn"
                onClick={onClose}
                aria-label="Close AEGIS"
              >
                <i className="fas fa-times" />
              </button>
            )}
          </header>
          {!collapsed && floating && <AegisChartSection model={model} />}
          {!collapsed && <AegisChatHistory />}
        </div>
        {!collapsed && <AegisChatBody />}
      </aside>
    </div>
  )
}
